const STORAGE_KEY = 'invoice_navigation_data';
const BACK_URL_KEY = 'invoice_back_url';
const DRAFT_STORAGE_KEY = 'draft_invoice_navigation_data';
const DRAFT_BACK_URL_KEY = 'draft_invoice_back_url';

export const setInvoiceNavigationData = ({ tokens, queryUrl, hasMore, total }) => {
    if (typeof window === 'undefined') return;
    try {
        const data = { tokens, queryUrl, hasMore, total };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to save invoice navigation data:', e);
    }
};

// Store where the user came from for smart back button
export const setInvoiceBackUrl = (url) => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(BACK_URL_KEY, url);
    } catch (e) {
        console.error('Failed to save invoice back URL:', e);
    }
};

export const getInvoiceBackUrl = (defaultUrl = '/invoices/sent/') => {
    if (typeof window === 'undefined') return defaultUrl;
    try {
        return sessionStorage.getItem(BACK_URL_KEY) || defaultUrl;
    } catch (e) {
        return defaultUrl;
    }
};

// Draft invoice navigation
export const setDraftInvoiceNavigationData = ({ tokens, queryUrl, hasMore, total }) => {
    if (typeof window === 'undefined') return;
    try {
        const data = { tokens, queryUrl, hasMore, total };
        sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to save draft invoice navigation data:', e);
    }
};

export const getDraftInvoiceNavigationData = () => {
    if (typeof window === 'undefined') return { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    try {
        const stored = sessionStorage.getItem(DRAFT_STORAGE_KEY);
        return stored ? JSON.parse(stored) : { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    } catch (e) {
        console.error('Failed to get draft invoice navigation data:', e);
        return { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    }
};

export const appendDraftInvoiceTokens = (newTokens) => {
    if (typeof window === 'undefined') return;
    try {
        const data = getDraftInvoiceNavigationData();
        const existingSet = new Set(data.tokens);
        const uniqueNewTokens = newTokens.filter(t => !existingSet.has(t));
        data.tokens = [...data.tokens, ...uniqueNewTokens];
        sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to append draft invoice tokens:', e);
    }
};

export const updateDraftNavigationMeta = ({ hasMore, total, queryUrl }) => {
    if (typeof window === 'undefined') return;
    try {
        const data = getDraftInvoiceNavigationData();
        if (hasMore !== undefined) data.hasMore = hasMore;
        if (total !== undefined) data.total = total;
        if (queryUrl !== undefined) data.queryUrl = queryUrl;
        sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to update draft navigation meta:', e);
    }
};

export const getDraftInvoiceNavigation = (currentId) => {
    const { tokens, hasMore, queryUrl } = getDraftInvoiceNavigationData();
    const currentIdStr = String(currentId);
    if (!tokens.length || !currentId) {
        return { prev: null, next: null, currentIndex: -1, total: 0, hasMore: false, queryUrl: null };
    }

    const currentIndex = tokens.findIndex(t => String(t) === currentIdStr);
    if (currentIndex === -1) {
        return { prev: null, next: null, currentIndex: -1, total: tokens.length, hasMore, queryUrl };
    }

    const isAtEnd = currentIndex === tokens.length - 1;

    return {
        prev: currentIndex > 0 ? tokens[currentIndex - 1] : null,
        next: currentIndex < tokens.length - 1 ? tokens[currentIndex + 1] : null,
        currentIndex,
        total: tokens.length,
        hasMore,
        queryUrl,
        needsMoreData: isAtEnd && hasMore
    };
};

export const setDraftInvoiceBackUrl = (url) => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(DRAFT_BACK_URL_KEY, url);
    } catch (e) {
        console.error('Failed to save draft invoice back URL:', e);
    }
};

export const getDraftInvoiceBackUrl = (defaultUrl = '/invoices') => {
    if (typeof window === 'undefined') return defaultUrl;
    try {
        return sessionStorage.getItem(DRAFT_BACK_URL_KEY) || defaultUrl;
    } catch (e) {
        return defaultUrl;
    }
};

export const getInvoiceNavigationData = () => {
    if (typeof window === 'undefined') return { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    } catch (e) {
        console.error('Failed to get invoice navigation data:', e);
        return { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    }
};

export const appendInvoiceTokens = (newTokens) => {
    if (typeof window === 'undefined') return;
    try {
        const data = getInvoiceNavigationData();
        const existingSet = new Set(data.tokens);
        const uniqueNewTokens = newTokens.filter(t => !existingSet.has(t));
        data.tokens = [...data.tokens, ...uniqueNewTokens];
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to append invoice tokens:', e);
    }
};

export const updateNavigationMeta = ({ hasMore, total, queryUrl }) => {
    if (typeof window === 'undefined') return;
    try {
        const data = getInvoiceNavigationData();
        if (hasMore !== undefined) data.hasMore = hasMore;
        if (total !== undefined) data.total = total;
        if (queryUrl !== undefined) data.queryUrl = queryUrl;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to update navigation meta:', e);
    }
};

export const getInvoiceNavigation = (currentToken) => {
    const { tokens, hasMore, queryUrl } = getInvoiceNavigationData();
    if (!tokens.length || !currentToken) {
        return { prev: null, next: null, currentIndex: -1, total: 0, hasMore: false, queryUrl: null };
    }

    const currentIndex = tokens.indexOf(currentToken);
    if (currentIndex === -1) {
        return { prev: null, next: null, currentIndex: -1, total: tokens.length, hasMore, queryUrl };
    }

    const isAtEnd = currentIndex === tokens.length - 1;

    return {
        prev: currentIndex > 0 ? tokens[currentIndex - 1] : null,
        next: currentIndex < tokens.length - 1 ? tokens[currentIndex + 1] : null,
        currentIndex,
        total: tokens.length,
        hasMore,
        queryUrl,
        needsMoreData: isAtEnd && hasMore
    };
};

export const clearInvoiceNavigationList = () => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        console.error('Failed to clear invoice navigation list:', e);
    }
};

// Legacy support
export const setInvoiceNavigationList = (invoiceTokens) => {
    setInvoiceNavigationData({ tokens: invoiceTokens, queryUrl: null, hasMore: false, total: invoiceTokens.length });
};

export const getInvoiceNavigationList = () => {
    return getInvoiceNavigationData().tokens;
};

// Transaction navigation
const TRANSACTION_STORAGE_KEY = 'transaction_navigation_data';

export const setTransactionNavigationData = ({ tokens, queryUrl, hasMore, total }) => {
    if (typeof window === 'undefined') return;
    try {
        const data = { tokens, queryUrl, hasMore, total };
        sessionStorage.setItem(TRANSACTION_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to save transaction navigation data:', e);
    }
};

export const getTransactionNavigationData = () => {
    if (typeof window === 'undefined') return { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    try {
        const stored = sessionStorage.getItem(TRANSACTION_STORAGE_KEY);
        return stored ? JSON.parse(stored) : { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    } catch (e) {
        console.error('Failed to get transaction navigation data:', e);
        return { tokens: [], queryUrl: null, hasMore: false, total: 0 };
    }
};

export const appendTransactionTokens = (newTokens) => {
    if (typeof window === 'undefined') return;
    try {
        const data = getTransactionNavigationData();
        const existingSet = new Set(data.tokens.map(t => String(t)));
        const uniqueNewTokens = newTokens.filter(t => !existingSet.has(String(t)));
        data.tokens = [...data.tokens, ...uniqueNewTokens];
        sessionStorage.setItem(TRANSACTION_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to append transaction tokens:', e);
    }
};

export const updateTransactionNavigationMeta = ({ hasMore, total, queryUrl }) => {
    if (typeof window === 'undefined') return;
    try {
        const data = getTransactionNavigationData();
        if (hasMore !== undefined) data.hasMore = hasMore;
        if (total !== undefined) data.total = total;
        if (queryUrl !== undefined) data.queryUrl = queryUrl;
        sessionStorage.setItem(TRANSACTION_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Failed to update transaction navigation meta:', e);
    }
};

export const getTransactionNavigation = (currentId) => {
    const { tokens, hasMore, queryUrl } = getTransactionNavigationData();
    const currentIdStr = String(currentId);
    if (!tokens.length || !currentId) {
        return { prev: null, next: null, currentIndex: -1, total: 0, hasMore: false, queryUrl: null };
    }

    const currentIndex = tokens.findIndex(t => String(t) === currentIdStr);
    if (currentIndex === -1) {
        return { prev: null, next: null, currentIndex: -1, total: tokens.length, hasMore, queryUrl };
    }

    const isAtEnd = currentIndex === tokens.length - 1;

    return {
        prev: currentIndex > 0 ? tokens[currentIndex - 1] : null,
        next: currentIndex < tokens.length - 1 ? tokens[currentIndex + 1] : null,
        currentIndex,
        total: tokens.length,
        hasMore,
        queryUrl,
        needsMoreData: isAtEnd && hasMore
    };
};
